import type { LandingStorySnapshot } from './landingStorySnapshot';
import type { LandingHandoffContext } from '@/shared/navigation/landingHandoffContext';

import { appRoutePath, appRoutePatterns } from '@/shared/navigation/appRoutePatterns';
export type LandingOwnerDestination =
  | 'browse'
  | 'measurements'
  | 'operations'
  | 'implement'
  | 'watch'
  | 'settings';

interface LandingOwnerHandoffInput {
  destination: LandingOwnerDestination;
  propertyId: string;
  story: LandingStorySnapshot | null;
  inboundContext: LandingHandoffContext;
  returnTo: string;
  skuId?: string;
}

function appendServedContext(
  params: URLSearchParams,
  story: LandingStorySnapshot | null,
  inbound: LandingHandoffContext,
): void {
  const landingContextRef = story?.immutableContextRef ?? inbound.immutableContextRef;
  const returnContextRef = inbound.returnContextRef
    ?? inbound.immutableContextRef
    ?? story?.immutableContextRef;
  if (landingContextRef) params.set('landing_context_ref', landingContextRef);
  if (returnContextRef) params.set('return_context_ref', returnContextRef);

  appendSourceSnapshotRefs(params, story, inbound.sourceSnapshotRefs);
  appendScenarioRefs(params, story, inbound.scenarioRefs);

  const firstPlan = inbound.planId ?? story?.planning.alternatives[0]?.alternativeId;
  if (firstPlan) params.set('plan_id', firstPlan);
  const decodeJobRef = inbound.decodeJobId ?? story?.implement.offerExtractionStatus.jobRef;
  if (decodeJobRef) params.set('decode_job_ref', decodeJobRef);
}

function appendSourceSnapshotRefs(
  params: URLSearchParams,
  story: LandingStorySnapshot | null,
  inboundRefs: string[],
): void {
  const sourceSnapshotRefs = new Set(inboundRefs);
  if (story) {
    sourceSnapshotRefs.add(story.firstPicture.sourceDigest);
    for (const alternative of story.planning.alternatives) {
      if (alternative.sourceSnapshotRef) sourceSnapshotRefs.add(alternative.sourceSnapshotRef);
    }
  }
  for (const sourceSnapshotRef of sourceSnapshotRefs) {
    params.append('source_snapshot_ref', sourceSnapshotRef);
  }
}

function appendScenarioRefs(
  params: URLSearchParams,
  story: LandingStorySnapshot | null,
  inboundRefs: string[],
): void {
  const scenarioRefs = new Set(inboundRefs);
  for (const alternative of story?.planning.alternatives ?? []) {
    scenarioRefs.add(alternative.alternativeId);
  }
  for (const scenarioRef of scenarioRefs) params.append('scenario_ref', scenarioRef);
}

/**
 * One destination table for every Landing handoff. Routes receive the served
 * immutable refs as query context; they remain the sole owner of the decision UI.
 */
export function buildLandingOwnerHandoff({
  destination,
  propertyId,
  story,
  inboundContext,
  returnTo,
  skuId,
}: LandingOwnerHandoffInput): string {
  const paths: Record<LandingOwnerDestination, () => string> = {
    browse: () => appRoutePatterns.browseProperties,
    measurements: () => appRoutePath(appRoutePatterns.measurementProperty, { propertyId }),
    operations: () => appRoutePath(appRoutePatterns.operationsPlanning, { propertyId }),
    implement: () => appRoutePath(appRoutePatterns.implement, { propertyId }),
    watch: () => appRoutePatterns.watch,
    settings: () => appRoutePatterns.settings,
  };
  const params = new URLSearchParams();
  params.set('property', propertyId);
  params.set('return_to', returnTo);
  appendServedContext(params, story, inboundContext);
  if (destination === 'settings') {
    params.set('section', 'packages');
    if (skuId) params.set('sku', skuId);
  }
  return `${paths[destination]()}?${params.toString()}`;
}
